import React, { useEffect } from "react";
import {
  Avatar,
  Box,
  Button,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  HStack,
  Icon,
  IconButton,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Stack,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { HamburgerIcon, ChevronDownIcon } from "@chakra-ui/icons";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { FaHome, FaInfoCircle, FaHistory, FaBook, FaMagic, FaUser, FaShieldAlt, FaSignOutAlt } from "react-icons/fa";
import { useAuth } from "@context/AuthContext";
import Logo from "@ui/assets/common/Logo";
import { PROJECT_NAME } from "@constants";
import { HEADER_LOGO_TEXT } from "@features/layout/layoutConstants";
import { colors, borderRadius } from "@theme/tokens";

const navItems = [
  { label: "Главная", to: "/", icon: FaHome, public: true },
  { label: "Workspace", to: "/workspace", icon: FaHome, public: false },
  { label: "Аналитика", to: "/analytics", icon: FaHistory, public: false },
  { label: "Правила", to: "/marketplace", icon: FaBook, public: false },
  { label: "Оптимизация", to: "/optimizer", icon: FaMagic, public: false },
  { label: "О проекте", to: "/info", icon: FaInfoCircle, public: true },
];

// ─── Nav Item ──────────────────────────────────────────────────────────────────

function NavItem({ item, location }) {
  const isActive = location.pathname === item.to;
  return (
    <Link
      as={NavLink}
      to={item.to}
      px={3}
      py={2}
      fontSize="14px"
      fontWeight="500"
      borderRadius={borderRadius.sm}
      color={isActive ? colors.brand.primary : colors.text.secondary}
      bg={isActive ? "rgba(66,227,180,0.08)" : "transparent"}
      _hover={{ textDecoration: "none", color: colors.text.primary, bg: "rgba(255,255,255,0.05)" }}
      transition="all 0.15s"
    >
      <HStack spacing={3} align="center" minH="24px">
        <Box
          w="24px"
          h="24px"
          borderRadius="full"
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
          mt="1px"
          bg={isActive ? "rgba(66,227,180,0.18)" : "rgba(255,255,255,0.06)"}
          border="1px solid"
          borderColor={isActive ? "rgba(66,227,180,0.45)" : "rgba(255,255,255,0.1)"}
          boxShadow={isActive ? "0 0 16px rgba(66,227,180,0.22)" : "none"}
          transition="all 0.2s"
        >
          <Icon as={item.icon} boxSize={2.5} color={isActive ? colors.brand.primary : colors.text.tertiary} />
        </Box>
        <Text lineHeight="20px" display="flex" alignItems="center">
          {item.label}
        </Text>
      </HStack>
    </Link>
  );
}

// ─── Profile Dropdown ──────────────────────────────────────────────────────────

function ProfileMenu({ user, userLabel, isAdmin, navigate, logout }) {
  const menuItemStyle = {
    fontSize: "14px",
    fontWeight: "500",
    color: colors.text.secondary,
    bg: "transparent",
    borderRadius: borderRadius.sm,
    py: 2,
    px: 3,
    _hover: { bg: "rgba(255,255,255,0.07)", color: colors.text.primary },
    _focus: { bg: "rgba(255,255,255,0.07)", color: colors.text.primary },
  };

  return (
    <Menu placement="bottom-end" offset={[0, 8]}>
      <MenuButton
        as={Button}
        variant="ghost"
        px={{ base: 1.5, md: 3 }}
        py={1}
        h="auto"
        borderRadius="999px"
        border="1px solid transparent"
        _hover={{ bg: "rgba(255,255,255,0.06)", borderColor: colors.border.default }}
        _active={{ bg: "rgba(255,255,255,0.09)" }}
        _expanded={{ borderColor: colors.border.light }}
        transition="all 0.15s"
      >
        <HStack spacing={{ base: 1.5, md: 2 }}>
          <Avatar
            size="xs"
            name={userLabel}
            bg={colors.brand.maroon}
            color="white"
            fontWeight="700"
            fontSize="11px"
          />
          <Stack spacing={0} display={{ base: "none", md: "flex" }} align="start">
            <Text fontSize="sm" fontWeight="600" color={colors.text.primary} lineHeight="1.2">
              {user?.first_name || "Пользователь"}
            </Text>
            <Text fontSize="10px" color={colors.text.muted} lineHeight="1.2">
              {user?.email}
            </Text>
          </Stack>
          <ChevronDownIcon fontSize="10px" color={colors.text.muted} display={{ base: "none", md: "block" }} />
        </HStack>
      </MenuButton>

      <Portal>
        <MenuList
          bg={colors.background.card}
          backdropFilter="blur(16px)"
          border="1px solid"
          borderColor={colors.border.light}
          boxShadow="0 16px 48px rgba(0,0,0,0.6)"
          borderRadius={borderRadius.md}
          p={1.5}
          minW="190px"
        >
          {/* User info header inside dropdown */}
          <Box px={3} py={2} mb={1}>
            <Text fontSize="13px" fontWeight="600" color={colors.text.primary} noOfLines={1}>
              {user?.first_name || "Пользователь"}
            </Text>
            <Text fontSize="11px" color={colors.text.muted} noOfLines={1}>
              {user?.email}
            </Text>
          </Box>
          <Divider borderColor={colors.border.subtle} mb={1} />

          <MenuItem
            icon={<Icon as={FaUser} boxSize={3.5} />}
            onClick={() => navigate("/profile")}
            {...menuItemStyle}
          >
            Профиль
          </MenuItem>

          {isAdmin && (
            <MenuItem
              icon={<Icon as={FaShieldAlt} boxSize={3.5} />}
              onClick={() => navigate("/admin")}
              {...menuItemStyle}
            >
              Админ-панель
            </MenuItem>
          )}

          <Divider borderColor={colors.border.subtle} my={1} />

          <MenuItem
            icon={<Icon as={FaSignOutAlt} boxSize={3.5} />}
            onClick={logout}
            {...menuItemStyle}
            color={colors.error[400]}
            _hover={{ bg: "rgba(248,113,113,0.1)", color: colors.error[400] }}
          >
            Выйти
          </MenuItem>
        </MenuList>
      </Portal>
    </Menu>
  );
}

// ─── Mobile Drawer ─────────────────────────────────────────────────────────────

function MobileDrawer({ isOpen, onClose, isLoggedIn, user, userLabel, isAdmin, navigate, logout, visibleNavItems, location }) {
  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="xs">
      <DrawerOverlay bg="rgba(0,0,0,0.65)" backdropFilter="blur(4px)" />
      <DrawerContent bg={colors.background.card} borderLeft="1px solid" borderColor={colors.border.light} maxW="280px">
        <DrawerCloseButton color={colors.text.muted} top={4} right={4} />
        <DrawerHeader pb={3} borderBottomWidth="1px" borderColor={colors.border.subtle}>
          <HStack spacing={2.5}>
            <Logo boxSize={6} />
            <Text fontWeight="700" fontSize="md" color={colors.brand.primary}>{PROJECT_NAME}</Text>
          </HStack>
        </DrawerHeader>

        <DrawerBody p={4}>
          <VStack align="stretch" spacing={1}>
            {/* User block for authenticated */}
            {isLoggedIn && (
              <>
                <Box
                  p={3}
                  mb={2}
                  borderRadius={borderRadius.md}
                  bg="rgba(66,227,180,0.05)"
                  border="1px solid"
                  borderColor="rgba(66,227,180,0.15)"
                >
                  <HStack spacing={3}>
                    <Avatar
                      size="sm"
                      name={userLabel}
                      bg={colors.brand.maroon}
                      color="white"
                      fontWeight="700"
                      fontSize="12px"
                      flexShrink={0}
                    />
                    <Stack spacing={0} minW={0}>
                      <Text fontSize="sm" fontWeight="600" color={colors.text.primary} noOfLines={1}>
                        {user?.first_name || "Пользователь"}
                      </Text>
                      <Text fontSize="11px" color={colors.text.muted} noOfLines={1}>
                        {user?.email}
                      </Text>
                    </Stack>
                  </HStack>
                </Box>
                <Divider borderColor={colors.border.subtle} mb={1} />
              </>
            )}

            {/* Nav links */}
            {visibleNavItems.map((item) => {
              const isActive = location.pathname === item.to;
              return (
                <Button
                  key={item.to}
                  variant="ghost"
                  justifyContent="flex-start"
                  leftIcon={<Icon as={item.icon} boxSize={4} />}
                  fontSize="14px"
                  fontWeight={isActive ? "600" : "500"}
                  color={isActive ? colors.brand.primary : colors.text.secondary}
                  bg={isActive ? "rgba(66,227,180,0.08)" : "transparent"}
                  borderRadius={borderRadius.sm}
                  _hover={{ bg: "rgba(255,255,255,0.06)", color: colors.text.primary }}
                  onClick={() => navigate(item.to)}
                  h="40px"
                >
                  {item.label}
                </Button>
              );
            })}

            <Divider borderColor={colors.border.subtle} my={1} />

            {/* Auth actions */}
            {!isLoggedIn ? (
              <>
                <Button
                  variant="ghost"
                  justifyContent="flex-start"
                  fontSize="14px"
                  color={colors.text.secondary}
                  onClick={() => navigate("/signin")}
                  h="40px"
                >
                  Войти
                </Button>
                <Button
                  bg={colors.brand.primary}
                  color={colors.text.inverted}
                  fontWeight="600"
                  fontSize="14px"
                  _hover={{ bg: colors.brand.primaryHover }}
                  onClick={() => navigate("/signup")}
                  h="40px"
                >
                  Регистрация
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  justifyContent="flex-start"
                  leftIcon={<Icon as={FaUser} boxSize={3.5} />}
                  fontSize="14px"
                  color={colors.text.secondary}
                  _hover={{ bg: "rgba(255,255,255,0.06)", color: colors.text.primary }}
                  onClick={() => navigate("/profile")}
                  h="40px"
                >
                  Профиль
                </Button>
                {isAdmin && (
                  <Button
                    variant="ghost"
                    justifyContent="flex-start"
                    leftIcon={<Icon as={FaShieldAlt} boxSize={3.5} />}
                    fontSize="14px"
                    color={colors.text.secondary}
                    _hover={{ bg: "rgba(255,255,255,0.06)", color: colors.text.primary }}
                    onClick={() => navigate("/admin")}
                    h="40px"
                  >
                    Админ-панель
                  </Button>
                )}
                <Button
                  variant="ghost"
                  justifyContent="flex-start"
                  leftIcon={<Icon as={FaSignOutAlt} boxSize={3.5} />}
                  fontSize="14px"
                  color={colors.error[400]}
                  _hover={{ bg: "rgba(248,113,113,0.1)", color: colors.error[400] }}
                  onClick={logout}
                  h="40px"
                >
                  Выйти
                </Button>
              </>
            )}
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

// ─── Header ────────────────────────────────────────────────────────────────────

function Header() {
  const { isAuthenticated, logout, user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const isLoggedIn = isAuthenticated && user;
  const userLabel = user?.first_name || user?.email || "Профиль";

  useEffect(() => {
    onClose();
  }, [location.pathname, onClose]);

  const visibleNavItems = navItems.filter((item) => item.public || isLoggedIn);

  return (
    <>
      <Box
        position="sticky"
        top={0}
        zIndex={100}
        backdropFilter="blur(12px)"
        bg="rgba(10,10,10,0.75)"
        borderBottom="1px solid"
        borderColor="rgba(255,255,255,0.06)"
      >
        <Box maxW="1280px" mx="auto" px={{ base: 4, md: 6 }}>
          <Box
            display="grid"
            gridTemplateColumns="auto 1fr auto"
            alignItems="center"
            h={{ base: "56px", md: "64px" }}
          >
            {/* Logo */}
            <HStack spacing={3}>
              <Link
                as={NavLink}
                to="/"
                display="flex"
                alignItems="center"
                gap={2.5}
                _hover={{ textDecoration: "none" }}
              >
                <Logo boxSize={{ base: 7, md: 8 }} />
                <Stack spacing={0.5} lineHeight="0.95">
                  <Text fontWeight="700" fontSize={{ base: "md", md: "lg" }} color={colors.brand.primary}>
                    {PROJECT_NAME}
                  </Text>
                  <Text
                    fontSize="9px"
                    letterSpacing="0.1em"
                    color={colors.text.muted}
                    display={{ base: "none", sm: "block" }}
                    noOfLines={1}
                    lineHeight="1.05"
                    maxW="150px"
                  >
                    {HEADER_LOGO_TEXT}
                  </Text>
                </Stack>
              </Link>
            </HStack>

            {/* Desktop nav (center) */}
            <HStack justify="center" spacing={1} display={{ base: "none", lg: "flex" }}>
              {visibleNavItems.map((item) => (
                <NavItem key={item.to} item={item} location={location} />
              ))}
            </HStack>

            {/* Right side actions */}
            <HStack justify="flex-end" spacing={{ base: 1.5, md: 2 }}>
              {!isLoggedIn ? (
                <>
                  <Button
                    as={NavLink}
                    to="/signin"
                    size="sm"
                    variant="ghost"
                    color={colors.text.secondary}
                    display={{ base: "none", md: "flex" }}
                    _hover={{ color: colors.text.primary }}
                  >
                    Войти
                  </Button>
                  <Button
                    as={NavLink}
                    to="/signup"
                    size="sm"
                    bg={colors.brand.primary}
                    color={colors.text.inverted}
                    fontWeight="600"
                    _hover={{ bg: colors.brand.primaryHover }}
                    display={{ base: "none", md: "flex" }}
                  >
                    Регистрация
                  </Button>
                </>
              ) : (
                <ProfileMenu
                  user={user}
                  userLabel={userLabel}
                  isAdmin={isAdmin}
                  navigate={navigate}
                  logout={logout}
                />
              )}

              {/* Hamburger — mobile/tablet */}
              <IconButton
                display={{ base: "flex", lg: "none" }}
                icon={<HamburgerIcon boxSize={4} />}
                variant="ghost"
                size="sm"
                color={colors.text.muted}
                _hover={{ bg: "rgba(255,255,255,0.08)", color: colors.text.primary }}
                onClick={onOpen}
                aria-label="Открыть меню"
              />
            </HStack>
          </Box>
        </Box>
      </Box>

      <MobileDrawer
        isOpen={isOpen}
        onClose={onClose}
        isLoggedIn={isLoggedIn}
        user={user}
        userLabel={userLabel}
        isAdmin={isAdmin}
        navigate={navigate}
        logout={logout}
        visibleNavItems={visibleNavItems}
        location={location}
      />
    </>
  );
}

export default Header;
